// Lista de uma aba (Recebidos ou Enviados) da tela `/app/splits` (WEB-123): filtros de
// status e período, os quatro estados (carregando, erro, vazio, filtro sem resultado) e a
// paginação por cursor. No molde das listas do painel: um `Card` só, com a barra de filtros
// em cima, as linhas separadas por fio e o "carregar mais" no rodapé.
//
// Os filtros são `<select>` NATIVOS (roda do celular, teclado e leitor de tela de graça) na
// casca dos campos do sistema (`FilterSelect`, abaixo), a mesma do `Select` do Radix fechado.

import type { ReactNode, SelectHTMLAttributes } from 'react';
import type { Split, SplitRole, SplitStatus } from '../../api/queries/splits.ts';
import { EmptyState } from '../../components/app/empty-state.tsx';
import { ErrorState } from '../../components/app/error-state.tsx';
import { ListSkeleton } from '../../components/app/list-skeleton.tsx';
import { ListToolbar } from '../../components/app/list-toolbar.tsx';
import { ChevronDown, Inbox, SearchX } from '../../components/icons/index.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { fieldShell } from '../../components/ui/input.tsx';
import { cn } from '../../lib/cn.ts';
import { isSplitStatus, SPLIT_STATUS, SPLIT_STATUSES } from './model.ts';
import { SplitRow } from './split-row.tsx';

export const PERIODS = ['7d', '30d', '90d'] as const;
export type Period = (typeof PERIODS)[number];

const PERIOD_LABEL: Record<Period | 'all', string> = {
  all: 'Todo o período',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  '90d': 'Últimos 90 dias',
};

export function isPeriod(value: string): value is Period {
  return PERIODS.some((period) => period === value);
}

const COPY: Record<
  SplitRole,
  { hint: string; empty: string; emptyHint: string; listLabel: string }
> = {
  received: {
    hint: 'Parte das vendas de outros sellers que cai no seu saldo quando o PIX é pago.',
    empty: 'Nenhum split recebido ainda',
    emptyHint:
      'Você não precisa autorizar nada: quem vende cadastra o seu e-mail na conta dele e divide a venda com você.',
    listLabel: 'Splits recebidos',
  },
  paid: {
    hint: 'Parte das suas cobranças que vai para outros sellers.',
    empty: 'Nenhum split enviado ainda',
    emptyHint:
      'Você divide uma venda na Nova cobrança, com os e-mails que cadastrou na aba Autorizações.',
    listLabel: 'Splits enviados',
  },
};

/**
 * `<select>` nativo na casca dos campos (`fieldShell`) com o chevron do gatilho do `Select`
 * do Radix. Local porque o painel do lojista não importa de `components/kit` (o nativo de lá
 * é o mesmo desenho); candidato a `components/ui` se outra tela precisar.
 */
function FilterSelect({
  className,
  children,
  ...props
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & { className?: string }) {
  return (
    <div
      className={cn(
        fieldShell,
        'relative flex items-center hover:bg-secondary focus-within:bg-card has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-ring md:text-13',
        className,
      )}
    >
      <select
        className="min-w-0 flex-1 cursor-pointer appearance-none self-stretch bg-transparent py-0 pr-9 pl-3 text-foreground outline-none"
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 size-4 text-muted-foreground"
      />
    </div>
  );
}

export interface SplitsListFilters {
  status: SplitStatus | undefined;
  period: Period | undefined;
}

export function SplitsList({
  side,
  rows,
  isLoading,
  error,
  onRetry,
  filters,
  onFiltersChange,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  selectedId,
  emptyAction,
  transactionLink,
}: {
  side: SplitRole;
  rows: Split[];
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  filters: SplitsListFilters;
  onFiltersChange: (next: SplitsListFilters) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (split: Split) => void;
  selectedId?: string | undefined;
  /** Ação do estado vazio (sem filtro): o caminho para começar a receber ou enviar. */
  emptyAction?: ReactNode;
  /** Link para a transação, só na aba Enviados. */
  transactionLink?: (transactionId: string) => ReactNode;
}) {
  const copy = COPY[side];
  const filtered = filters.status !== undefined || filters.period !== undefined;
  const failed = error !== null && error !== undefined && rows.length === 0;

  return (
    <Card className="rise-in gap-0 py-0" style={{ animationDelay: '120ms' }}>
      <div className="grid gap-3 p-3.5 sm:p-5">
        <p className="max-w-prose text-13 text-muted-foreground">{copy.hint}</p>
        <ListToolbar
          {...(rows.length > 0
            ? {
                right: `${rows.length}${hasMore ? '+' : ''} ${rows.length === 1 ? 'split' : 'splits'}`,
              }
            : {})}
        >
          <FilterSelect
            aria-label="Status"
            className="max-sm:flex-1"
            value={filters.status ?? 'all'}
            onChange={(event) => {
              const value = event.target.value;
              onFiltersChange({ ...filters, status: isSplitStatus(value) ? value : undefined });
            }}
          >
            <option value="all">Todos os status</option>
            {SPLIT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {SPLIT_STATUS[status].label}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            aria-label="Período"
            className="max-sm:flex-1"
            value={filters.period ?? 'all'}
            onChange={(event) => {
              const value = event.target.value;
              onFiltersChange({ ...filters, period: isPeriod(value) ? value : undefined });
            }}
          >
            <option value="all">{PERIOD_LABEL.all}</option>
            {PERIODS.map((period) => (
              <option key={period} value={period}>
                {PERIOD_LABEL[period]}
              </option>
            ))}
          </FilterSelect>
        </ListToolbar>
      </div>

      <div className="border-border border-t">
        {failed ? (
          <div className="p-3.5 sm:p-5">
            <ErrorState error={error} onRetry={onRetry} />
          </div>
        ) : isLoading ? (
          <div role="status" aria-busy="true" aria-label="Carregando splits">
            <ListSkeleton rows={4} bare />
          </div>
        ) : rows.length === 0 ? (
          filtered ? (
            <EmptyState
              bare
              icon={SearchX}
              title="Nenhum split com esses filtros."
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onFiltersChange({ status: undefined, period: undefined });
                  }}
                >
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <EmptyState
              bare
              icon={Inbox}
              title={copy.empty}
              description={copy.emptyHint}
              action={emptyAction}
            />
          )
        ) : (
          <ul aria-label={copy.listLabel}>
            {rows.map((split) => (
              <li key={split.id} className="border-border border-t first:border-t-0">
                <SplitRow
                  split={split}
                  side={side}
                  selected={split.id === selectedId}
                  onSelect={() => {
                    onSelect(split);
                  }}
                  {...(transactionLink === undefined
                    ? {}
                    : { transactionLink: transactionLink(split.transaction.id) })}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center border-border border-t px-5 py-3">
          <Button variant="outline" size="sm" disabled={isLoadingMore} onClick={onLoadMore}>
            {isLoadingMore ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </div>
      )}
    </Card>
  );
}
